import { useCallback, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import type { Plan, PlanStarter, Program, ProgramModel } from "@heraion/shared";
import { CreateHub, CreateHubActions, resolveTheme, useHubLibrarySearch } from "@heraion/ui";

import { CreateHubLibraryWeb } from "./CreateHubLibraryWeb";
import { CreateHubStartWeb } from "./CreateHubStartWeb";
import type { Experience } from "./experience";
import { useThemePalette } from "./theme";

// Web glue for the shared Create-hub (HR-S4w) — the sidebar "Crea" CTA's
// twin of mobile's useCreateHubNav. Owns the hub open/close state and
// translates hub intents into composer requests AppShell routes to the Plan,
// Library (CO-19, coach) or Workouts surface (state-based nav — no deep
// links on web). RW-18: "New workout" now opens the lifted Logger via
// WorkoutsScreen's own composer-request handling, the same pattern the Plan
// surface already used for program/plan requests. "Repeat workout" stays
// unwired — CreateHubActions renders only the handlers it receives
// (capability gating by omission).

export type ComposerRequest =
  // CO-17/CO-20 — `authoredAs` rides along ONLY on a create (no
  // program/plan attached): the Experience active when the hub was opened,
  // stamped by this hook below. An edit (CreateHubLibraryWeb's
  // onEditProgram/onEditPlan) never sets it — same "doesn't change hats"
  // contract as mobile's useCreateHubNav.
  // `model`: «Plan a program» after «Save as model», the wizard on it.
  | { kind: "program"; program?: Program; authoredAs?: Program["authoredAs"]; model?: Omit<ProgramModel, "id"> }
  | { kind: "plan"; plan?: Plan; authoredAs?: Program["authoredAs"] }
  // RW-18: the logger is a web surface now — "New workout" opens it straight
  // from the hub, same as mobile's useCreateHubNav. "Repeat workout" stays
  // unwired (onRepeatWorkout omitted below): it needs the full past-session
  // Workout + patternsBySlug hydrate the web console doesn't fetch yet, a
  // separate follow-up. `planStarter` (added for the Plan-tab "Start"
  // wiring, see useWorkoutStarter.ts) opens the Logger PRE-POPULATED from a
  // program session instead of blank — omitted for a plain "New workout".
  | { kind: "workout"; planStarter?: PlanStarter };

export function useCreateHubWeb(
  // AppShell passes `user.sub` (always resolved by the time this hub
  // mounts), but the type stays `string | null` for coherence with
  // LibraryBrowser's own callerSub — see useLibrary.ts's header.
  callerSub: string | null,
  // CO-19 — the Experience AppShell mounted this hub for. Decides the
  // `authoredAs` stamped on a create (below); AppShell's own onComposer
  // callback (which owns the tab state) decides which surface a coach's
  // program/plan request lands on ("library" instead of "plan" — coach has
  // no Plan surface, next/coaching.md), the same "reachable from the active
  // experience" fix as mobile's useCreateHubNav.
  experience: Experience,
  onComposer: (req: ComposerRequest) => void,
  // CO-21 — AssignSheet's roster for the hub's own library section, straight
  // pass-through from AppShell's single useCoachStatus read (same value fed
  // to CoachLibraryScreen/PlanScreen — CN-coach-edges-fetch, no second fetch
  // here).
  athleteRoster?: ReadonlyArray<string>,
): {
  openHub: () => void;
  closeHub: () => void;
  hubNode: ReactElement;
} {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  // MX-8b: the hub's own library search (tab + tag filters), lifted above
  // CreateHubLibraryWeb so a wide/narrow resize while the hub is open
  // survives it (LibraryBrowser's own internal state would otherwise reset
  // on the remount that crossing the breakpoint causes). Reset on every path
  // that closes the hub, matching today's behaviour (the hub unmounts
  // CreateHubLibraryWeb while hidden, which already dropped this on close).
  const {
    filter: libraryFilter,
    setFilter: setLibraryFilter,
    tagFilters: libraryTagFilters,
    setTagFilters: setLibraryTagFilters,
    reset: resetLibrarySearch,
  } = useHubLibrarySearch();

  const openHub = useCallback(() => setVisible(true), []);
  const closeHub = useCallback(() => {
    setVisible(false);
    resetLibrarySearch();
  }, [resetLibrarySearch]);

  const route = useCallback(
    (req: ComposerRequest) => {
      setVisible(false);
      resetLibrarySearch();
      onComposer(req);
    },
    [onComposer, resetLibrarySearch],
  );

  // AuthoredAs stamped on a CREATE only — Program["authoredAs"] rather than
  // a bespoke union so it can't drift from the schema's, same derivation as
  // mobile's useCreateHubNav. `experience` can also be "admin", which has no
  // create surface (AppShell's createCta gate), so this only ever actually
  // resolves "athlete" or "coach" in practice.
  const authoredAs: Program["authoredAs"] =
    experience === "coach" ? "coach" : "athlete";

  const hubNode = (
    <CreateHub
      theme={theme}
      visible={visible}
      onClose={closeHub}
      title={t("createHub.title", { defaultValue: "Create" })}
      closeLabel={t("createHub.close", { defaultValue: "Close" })}
    >
      {experience === "coach" ? (
        // CO-18 — same reason as mobile's useCreateHubNav: the coach
        // experience has no logger surface, so the logging rows are REMOVED
        // rather than left alive and inert.
        <CreateHubActions
          theme={theme}
          onPlanProgram={() => route({ kind: "program", authoredAs })}
          onPlanWorkout={() => route({ kind: "plan", authoredAs })}
        />
      ) : (
        // AP-9b: the athlete's block reads coverage and programs, so it
        // mounts only here, and only while the hub is open.
        <CreateHubStartWeb
          theme={theme}
          onNewWorkout={() => route({ kind: "workout" })}
          onPlanProgram={() => route({ kind: "program", authoredAs })}
          onPlanWorkout={() => route({ kind: "plan", authoredAs })}
          onStarter={(starter) => route({ kind: "workout", planStarter: starter })}
        />
      )}
      <CreateHubLibraryWeb
        theme={theme}
        dark={dark}
        callerSub={callerSub}
        // CO-19 — filters the hub's own personal-library section to 'coach'
        // when opened FROM the coach view, same rows CoachLibraryScreen's
        // surface shows. Athlete/admin pass `null` (LibraryBrowser's
        // default): unfiltered is today's behaviour, matching mobile's
        // identical call in useCreateHubNav.
        authoredAs={experience === "coach" ? "coach" : null}
        athleteRoster={athleteRoster}
        onEditProgram={(program) => route({ kind: "program", program })}
        onEditPlan={(plan) => route({ kind: "plan", plan })}
        onPlanFromModel={(model) => route({ kind: "program", authoredAs, model })}
        onStarter={(starter) => route({ kind: "workout", planStarter: starter })}
        filter={libraryFilter}
        onFilterChange={setLibraryFilter}
        tagFilters={libraryTagFilters}
        onTagFiltersChange={setLibraryTagFilters}
      />
    </CreateHub>
  );

  return { openHub, closeHub, hubNode };
}
